'use client';

import { useState } from 'react';

import { FcGoogle } from 'react-icons/fc';

import FormEmailAndPasswordBetterAuth from '@/features/auth/components/FormEmailAndPasswordBetterAuth';
import { messages } from '@/features/auth/localization/texts';
import { getPartner } from '@/features/v2/auth/register/actions/getPartner';
import { useAffiliateFromUrl } from '@/features/v2/auth/register/hooks/useAffiliateFromUrl';
import { useGoogleRegister } from '@/features/v2/auth/register/hooks/useGoogleRegister';
import { useOtpSheet } from '@/features/v2/auth/register/hooks/useOtpSheet';
import { useRegistration } from '@/features/v2/auth/register/hooks/useRegistration';
import { userUIStore } from '@/stores/userUIStore';

import { Vendors } from '@/enums/core';

import { OtpVerificationSheet } from '@/components/sheet/OtpVerificationSheet';
import { Button } from '@/components/ui/button';
import { useDetectCountry } from '@/src/queries/country/use-detect-country';

export type MarketingRegisterMethodsProps = {
  welcomeBonus: string;
};

export const MarketingRegisterMethods = ({
  welcomeBonus,
}: MarketingRegisterMethodsProps) => {
  const affiliateFromUrl = useAffiliateFromUrl();
  const [affiliate, setAffiliate] = useState(() => ({
    open: Boolean(affiliateFromUrl),
    code: affiliateFromUrl ?? '',
  }));

  const { data } = useDetectCountry();
  const detectedCountryCode = data?.countryCode ?? 'GLOBAL';
  const [selectedCountryCode, setSelectedCountryCode] = useState(detectedCountryCode);

  const partner = getPartner(affiliate.code);

  const {
    isOtpSheetOpen,
    registeredEmail,
    openOtpSheet,
    closeOtpSheet,
    handleOtpVerificationSuccess,
  } = useOtpSheet();

  const {
    isCollectingFingerprint,
    collectVisitorIdBeforeRegister,
    handleRegistrationSuccessWithOtp,
    handleRegistrationError,
  } = useRegistration({
    welcomeBonus,
    openOtpSheet,
  });

  const {
    state: googleRegisterState,
    handleGoogleRegister,
  } = useGoogleRegister({
    welcomeBonus,
    affiliateCode: affiliate.code,
    partner,
    selectedCountryCode,
  });

  // Aggregate Google-button pending here; email submit locks stay inside the form.
  const isGoogleButtonDisabled =
    isCollectingFingerprint || googleRegisterState === 'pending';

  const { toggleRegisterModal, toggleLoginModal } = userUIStore();

  return (
    <>
      <FormEmailAndPasswordBetterAuth
        isRegister
        onSuccessWithOtp={handleRegistrationSuccessWithOtp}
        onError={handleRegistrationError}
        onBeforeRegister={collectVisitorIdBeforeRegister}
        country={selectedCountryCode}
        onCountryChange={setSelectedCountryCode}
        gift={welcomeBonus}
        parent={Number(affiliateFromUrl) || 0}
        affiliatorId={affiliate.code}
        partner={partner || ''}
        vendor={Vendors.EMAIL}
      />

      <OtpVerificationSheet
        isOpen={isOtpSheetOpen}
        onClose={closeOtpSheet}
        email={registeredEmail}
        onSuccess={handleOtpVerificationSuccess}
      />

      <div className="h-[11px] relative tracking-[-0.01em] leading-[20px] inline-block mx-[17px] lg:hidden mb-5">
        <span>{messages.alreadyHaveAnAccount}</span>
        <span
          onClick={() => {
            toggleRegisterModal();
            toggleLoginModal();
          }}
          className="[text-decoration:underline] text-sivarbet-primary cursor-pointer"
        >
          {messages.enterHere}
        </span>
      </div>

      <div className="flex flex-col items-center justify-center gap-[10px] mx-[17px]">
        <p
          className="text-xs"
          onClick={() => {
            setAffiliate((current) => ({
              ...current,
              open: !current.open,
            }));
          }}
        >
          ¿Tienes un código de afiliado?
        </p>
        {affiliate.open && (
          <input
            type="text"
            placeholder="Código de afiliado"
            className="w-full! [border:none]! [outline:none]! bg-sivarbet-border! self-stretch! h-7! shadow-[0px_1px_2px_rgba(0,0,0,0.05)]! rounded-lg! flex! flex-row! justify-start! py-[15px]! px-3.5! box-border! !font-text-sm-medium font-medium! text-sm! text-slategray!"
            value={affiliate.code}
            onChange={(e) => {
              setAffiliate((current) => ({
                ...current,
                code: e.target.value,
              }));
            }}
          />
        )}
      </div>

      <div className="mx-auto text-center min-w-[104px] mt-5">
        {messages.orRegisterWith}
      </div>

      <div className="self-stretch flex flex-col items-start justify-start gap-[30px] text-mini text-third_text mt-5 mx-[17px]">
        <Button
          disabled={isGoogleButtonDisabled}
          onClick={handleGoogleRegister}
          type="button"
          className="register-button cursor-pointer [border:none] py-[10.5px] px-5 bg-sivarbet-background self-stretch rounded-lg overflow-hidden flex flex-row items-center justify-center gap-[8px]"
        >
          <FcGoogle className="text-lg" />
          <div className="w-[145px] flex items-center justify-center pt-[4.5px] px-0 pb-0 box-border">
            <div className="self-stretch h-2.5 relative text-sm leading-[20px] font-medium font-text-sm-medium text-sivarbet-primary text-center flex items-center justify-center">
              {`${messages.registerWith} ${messages.google}`}
            </div>
          </div>
        </Button>
      </div>
    </>
  );
};
